import * as React from 'react';
import { useFilters } from '@/hooks';
import { Typography, Icon } from '@/components/common';
import Icons from '@/const/icons';
import { useQuery } from '@tanstack/react-query';
import {
	GET_CLAIM_FORM_QUESTIONS,
	GET_CLIENT_STEPPER_STATUSES,
	GET_ELIGIBLE_STATES,
	GET_FOLLOW_UP,
	lawyerDirectoryService,
} from '@/api';
import { useRouter } from 'next/router';
import { parse, isValid, format } from 'date-fns';

export const ActiveFiltersPills = () => {
	const router = useRouter();
	const { id: caseId } = router.query;
	const { setParamFilter, setParamSeveralFilters, params } = useFilters();

	const { data: stepperData } = useQuery({
		queryKey: [GET_CLIENT_STEPPER_STATUSES, caseId],
		queryFn: () => lawyerDirectoryService.getClientSteps(caseId as string),
		enabled: !!caseId,
	});

	const { data: followUpsList } = useQuery({
		queryKey: [GET_FOLLOW_UP, caseId],
		queryFn: () => lawyerDirectoryService.getFollowUps(caseId as string),
		enabled: !!caseId,
	});

	const { data: claimFormList } = useQuery({
		queryKey: [GET_CLAIM_FORM_QUESTIONS, caseId],
		queryFn: () =>
			lawyerDirectoryService.getClaimFormQuestions(caseId as string),
		enabled: !!caseId,
	});

	const { data: eligibleStatesData } = useQuery({
		queryKey: [GET_ELIGIBLE_STATES, caseId],
		queryFn: () => lawyerDirectoryService.getEligibleStates(caseId as string),
		enabled: !!caseId,
	});

	const search = params.get('search');
	const step = params.get('step');
	const flagged = params.get('flagged');
	const flaggedReason = params.get('flaggedReason');
	const dateFrom = params.get('dateFrom');
	const dateTo = params.get('dateTo');
	const messageFilter = params.get('messageFilter');
	const documentFilter = params.get('documentFilter');
	const claimFormQuestions = params.get('claimFormQuestions');
	const documents = params.get('documents');
	const state = params.get('state');

	type Pill = { id: string; text: string; onRemove: () => void };
	const pills: Pill[] = [];

	if (search) {
		pills.push({
			id: 'search',
			text: `Search: ${search}`,
			onRemove: () => setParamFilter('search', undefined),
		});
	}

	if (step && stepperData?.data) {
		const stepIds = step.split(',');
		stepIds.forEach((id) => {
			const s = stepperData.data.find((item) => item.id.toString() === id);
			if (s) {
				pills.push({
					id: `step-${id}`,
					text: `Step: ${s.name}`,
					onRemove: () => {
						const newSteps = stepIds.filter((x) => x !== id);
						setParamFilter(
							'step',
							newSteps.length ? newSteps.join(',') : undefined
						);
					},
				});
			}
		});
	}

	if (flagged) {
		const text =
			flagged === 'true' ? 'Flag: All Flagged' : 'Flag: All Except Flagged';
		pills.push({
			id: 'flagged',
			text,
			onRemove: () => setParamFilter('flagged', undefined),
		});
	}

	if (flaggedReason) {
		const reasons = flaggedReason.split(',');
		reasons.forEach((reason) => {
			pills.push({
				id: `flaggedReason-${reason}`,
				text: `Flag: ${reason}`,
				onRemove: () => {
					const newReasons = reasons.filter((x) => x !== reason);
					setParamFilter(
						'flaggedReason',
						newReasons.length ? newReasons.join(',') : undefined
					);
				},
			});
		});
	}

	if (state && eligibleStatesData?.data) {
		const stateIds = state.split(',');
		stateIds.forEach((id) => {
			const s = eligibleStatesData.data.find(
				(item) => item.id.toString() === id
			);
			if (s) {
				pills.push({
					id: `state-${id}`,
					text: `State: ${s.name}`,
					onRemove: () => {
						const newStates = stateIds.filter((x) => x !== id);
						setParamFilter(
							'state',
							newStates.length ? newStates.join(',') : undefined
						);
					},
				});
			}
		});
	}

	if (dateFrom || dateTo) {
		const formatStr = (d: string) => {
			const parsed = parse(d, 'yyyy-MM-dd', new Date());
			return isValid(parsed) ? format(parsed, 'MM-dd-yyyy') : d;
		};
		let text = '';
		if (dateFrom && dateTo)
			text = `Activity: ${formatStr(dateFrom)} - ${formatStr(dateTo)}`;
		else if (dateFrom) text = `Activity: After ${formatStr(dateFrom)}`;
		else if (dateTo) text = `Activity: Before ${formatStr(dateTo)}`;

		pills.push({
			id: 'dates',
			text,
			onRemove: () =>
				setParamSeveralFilters([
					{ label: 'dateFrom', value: undefined },
					{ label: 'dateTo', value: undefined },
				]),
		});
	}

	if (messageFilter) {
		let text = '';
		if (messageFilter === 'UNREAD_MESSAGES') text = 'Messages: Unread';
		if (messageFilter === 'READ_MESSAGES') text = 'Messages: Replied to';
		if (text) {
			pills.push({
				id: 'messageFilter',
				text,
				onRemove: () => setParamFilter('messageFilter', undefined),
			});
		}
	}

	if (documents) {
		const docs = documents.split(',');
		docs.forEach((doc) => {
			pills.push({
				id: `documents-${doc}`,
				text: `Doc: ${doc}`,
				onRemove: () => {
					const newDocs = docs.filter((x) => x !== doc);
					setParamFilter(
						'documents',
						newDocs.length ? newDocs.join(',') : undefined
					);
				},
			});
		});
	}

	if (documentFilter && followUpsList?.data) {
		try {
			const parsed = JSON.parse(documentFilter as string) as {
				questionId: number;
				answers: string[];
			}[];
			parsed.forEach((item) => {
				for (const fu of followUpsList.data) {
					const q = fu.questions?.find((q: any) => q.id === item.questionId);
					if (q) {
						const truncated =
							q.text.length > 15 ? q.text.substring(0, 15) + '...' : q.text;
						pills.push({
							id: `doc-${item.questionId}`,
							text: `Follow-up: ${truncated}`,
							onRemove: () => {
								const newDocs = parsed.filter(
									(x) => x.questionId !== item.questionId
								);
								setParamFilter(
									'documentFilter',
									newDocs.length ? JSON.stringify(newDocs) : undefined
								);
							},
						});
						break;
					}
				}
			});
		} catch {
			// ignore malformed JSON
		}
	}

	if (claimFormQuestions && claimFormList?.data) {
		try {
			const parsed = JSON.parse(claimFormQuestions as string) as {
				questionId: number;
				answers: string[];
			}[];
			parsed.forEach((item) => {
				const q = claimFormList.data.find((q) => q.id === item.questionId);
				if (q) {
					const truncated =
						q.question.length > 15
							? q.question.substring(0, 15) + '...'
							: q.question;
					pills.push({
						id: `claim-form-${item.questionId}`,
						text: `Claim Form: ${truncated}`,
						onRemove: () => {
							const newQuestions = parsed.filter(
								(x) => x.questionId !== item.questionId
							);
							setParamFilter(
								'claimFormQuestions',
								newQuestions.length ? JSON.stringify(newQuestions) : undefined
							);
						},
					});
				}
			});
		} catch {
			// ignore malformed JSON
		}
	}

	return (
		<>
			{pills.map((pill) => (
				<div
					key={pill.id}
					className="inline-flex items-center gap-2 bg-[#8039DF1A] border border-[#8039DF33] rounded-lg px-3 py-1.5 text-sm text-primary max-w-[170px]"
				>
					<Typography type="regular-14" className="truncate text-primary">
						{pill.text}
					</Typography>
					<button type="button" onClick={pill.onRemove}>
						<Icon
							src={Icons.close}
							width={12}
							height={12}
							className="text-primary flex-shrink-0"
						/>
					</button>
				</div>
			))}
		</>
	);
};
